import { FLOW_STEPS, FlowPhase, FlowStep, FlowStepId, flowStepById } from './flowSteps';

/**
 * Reine Statusberechnung fuer den LinkedIn-Prozess: aus Zaehlern wird je
 * Schritt "erledigt", "bereit" oder "gesperrt". Ohne React, damit es sich
 * ohne Rendern testen laesst.
 */
export interface FlowData {
  strategies: number;
  activeStrategy: { id: string; name: string; linkedProfileIds: string[] } | null;
  profiles: number;
  seriesFormats: number;
  postIdeas: number;
  contentBriefs: number;
  editorialPlans: number;
  linkedInPosts: number;
  calendar: { total: number; scheduled: number; published: number };
  scheduledPosts: { pending: number; posted: number };
  linkedIn: { configured: boolean; connected: boolean };
  /** Zeitstempel des Ladens; fehlt bei EMPTY_FLOW_DATA. */
  loadedAt?: number;
}

export const EMPTY_FLOW_DATA: FlowData = {
  strategies: 0,
  activeStrategy: null,
  profiles: 0,
  seriesFormats: 0,
  postIdeas: 0,
  contentBriefs: 0,
  editorialPlans: 0,
  linkedInPosts: 0,
  calendar: { total: 0, scheduled: 0, published: 0 },
  scheduledPosts: { pending: 0, posted: 0 },
  linkedIn: { configured: false, connected: false },
};

export type StepState = 'done' | 'ready' | 'blocked';

export interface StepStatus {
  step: FlowStep;
  state: StepState;
  /** Anzahl der zum Schritt gehoerenden Datensaetze. */
  count: number;
  /** Fehlende harte Voraussetzungen. */
  missing: FlowStep[];
  /** Weiche Hinweise (fehlende Empfehlungen, fehlende Verbindung ...). */
  hints: string[];
}

export interface FlowStatus {
  steps: StepStatus[];
  /** Erster nicht erledigter Schritt in Prozessreihenfolge; null = alles erledigt. */
  nextStep: FlowStep | null;
  doneCount: number;
  progressPercent: number;
  /** Prozessweite Warnungen (z. B. keine aktive Strategie). */
  warnings: string[];
}

export function stepCount(data: FlowData, id: FlowStepId): number {
  switch (id) {
    case 'strategy': return data.strategies;
    case 'profiles': return data.profiles;
    case 'series': return data.seriesFormats;
    case 'ideas': return data.postIdeas;
    case 'plan': return data.editorialPlans;
    case 'briefs': return data.contentBriefs;
    case 'post': return data.linkedInPosts;
    case 'calendar': return data.calendar.total;
    case 'measure': return data.calendar.published + data.scheduledPosts.posted;
    default: return 0;
  }
}

function isDone(data: FlowData, id: FlowStepId): boolean {
  return stepCount(data, id) > 0;
}

function hintsFor(data: FlowData, step: FlowStep, done: (id: FlowStepId) => boolean): string[] {
  const hints: string[] = [];
  for (const rec of step.recommends) {
    if (!done(rec)) {
      const r = flowStepById(rec);
      hints.push(`Ohne ${r.label} fehlt der KI hier Kontext.`);
    }
  }
  if (step.id === 'post') {
    if (data.strategies > 0 && !data.activeStrategy) {
      hints.push('Keine Strategie ist als aktiv markiert – der Generator nimmt sonst irgendeine.');
    } else if (data.activeStrategy && data.activeStrategy.linkedProfileIds.length === 0 && data.profiles > 0) {
      hints.push('Die aktive Strategie hat keine Referenzprofile verknüpft.');
    }
  }
  if (step.id === 'measure' || step.id === 'calendar') {
    if (!data.linkedIn.configured) {
      hints.push('Die LinkedIn-Anbindung ist auf dem Server nicht konfiguriert; Veröffentlichen geht dann nur manuell.');
    } else if (!data.linkedIn.connected) {
      hints.push('Noch kein LinkedIn-Konto verbunden.');
    }
  }
  return hints;
}

export function computeFlowStatus(data: FlowData): FlowStatus {
  const done = (id: FlowStepId) => isDone(data, id);
  const steps: StepStatus[] = FLOW_STEPS.map((step) => {
    const missing = step.requires.filter((r) => !done(r)).map(flowStepById);
    // "erledigt" gewinnt vor "gesperrt": manuell angelegte Daten zaehlen.
    const state: StepState = done(step.id) ? 'done' : missing.length > 0 ? 'blocked' : 'ready';
    return {
      step,
      state,
      count: stepCount(data, step.id),
      missing,
      hints: hintsFor(data, step, done),
    };
  });

  const doneCount = steps.filter((s) => s.state === 'done').length;
  const nextStep = steps.find((s) => s.state !== 'done')?.step ?? null;

  const warnings: string[] = [];
  if (data.strategies > 0 && !data.activeStrategy) {
    warnings.push('Keine Strategie ist als aktiv markiert. Setze eine aktiv, damit Ideen und Beiträge den richtigen Kontext bekommen.');
  }
  if (data.linkedIn.configured && !data.linkedIn.connected && data.calendar.total > 0) {
    warnings.push('Es liegen Kalendereinträge vor, aber kein LinkedIn-Konto ist verbunden.');
  }

  return {
    steps,
    nextStep,
    doneCount,
    progressPercent: Math.round((doneCount / FLOW_STEPS.length) * 100),
    warnings,
  };
}

export function statusOf(status: FlowStatus, id: FlowStepId): StepStatus {
  return status.steps.find((s) => s.step.id === id) as StepStatus;
}

export interface PhaseStatus {
  phase: FlowPhase;
  /** erledigt = alle Schritte erledigt; gesperrt = der erste offene Schritt ist gesperrt. */
  state: StepState;
  steps: StepStatus[];
  /** Erster nicht erledigter Schritt der Phase; null = Phase komplett. */
  nextStep: StepStatus | null;
}

/** Zustand einer Phase aus den Zustaenden ihrer Schritte. */
export function phaseState(status: FlowStatus, phase: FlowPhase): PhaseStatus {
  const steps = phase.steps.map((id) => statusOf(status, id));
  const next = steps.find((s) => s.state !== 'done') ?? null;
  const state: StepState = next === null ? 'done' : next.state === 'blocked' ? 'blocked' : 'ready';
  return { phase, state, steps, nextStep: next };
}
